import { Accordion, AccordionSummary, AccordionDetails, Box, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useGetAssistantsQuery } from 'services/assistantApi';
import { useGetPromptsQuery } from 'services/promptApi';
import { useGetScriptsQuery } from 'services/scriptApi';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import MDTypography from 'components/MDTypography';
import { useNotification } from 'hook/useNotification';

export default function ExportConfigurationSelection({
  selectedAssistants,
  setSelectedAssistants,
  selectedPrompts,
  setSelectedPrompts,
  selectedScripts,
  setSelectedScripts
}) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const {
    data: assistants = [],
    isLoading: assistantsLoading,
    error: assistantsError
  } = useGetAssistantsQuery();
  const {
    data: prompts = [],
    isLoading: promptsLoading,
    error: promptsError
  } = useGetPromptsQuery();
  const {
    data: scripts = [],
    isLoading: scriptsLoading,
    error: scriptsError
  } = useGetScriptsQuery();

  const assistantColumns = [
    {
      field: 'id',
      headerName: msg({ id: 'page.assistants.table.column.id' }),
      width: 50
    },
    {
      field: 'code',
      headerName: msg({ id: 'page.assistants.table.column.code' }),
      minWidth: 350
    },
    {
      field: 'label',
      headerName: msg({ id: 'page.assistants.table.column.label' }),
      minWidth: 350
    },
    {
      field: 'isPublic',
      headerName: msg({ id: 'page.assistants.table.column.isPublic' }),
      type: 'boolean',
      width: 100
    },
    {
      field: 'creationDate',
      headerName: msg({ id: 'page.assistants.table.column.creationDate' }),
      renderCell: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
      minWidth: 150
    }
  ];

  const promptColumns = [
    {
      field: 'id',
      headerName: msg({ id: 'page.prompts.table.column.id' }),
      width: 50
    },
    {
      field: 'code',
      headerName: msg({ id: 'page.prompts.table.column.code' }),
      minWidth: 350
    },
    {
      field: 'description',
      headerName: msg({ id: 'page.prompts.table.column.description' }),
      minWidth: 350
    }
  ];

  const scriptColumns = [
    {
      field: 'code',
      headerName: msg({ id: 'page.scripts.table.column.code' }),
      minWidth: 300
    },
    {
      field: 'size',
      headerName: msg({ id: 'page.scripts.table.column.size' }),
      width: 120,
      renderCell: (params) => {
        if (params.value == null) return '';
        return `${params.value} B`;
      }
    }
  ];

  const dataError = assistantsError || promptsError || scriptsError;
  if (dataError) {
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.exportConfiguration.fetch.error' })
      });
    } catch {
      // Ignore exception
    }
  }

  const dataLoading = assistantsLoading || promptsLoading || scriptsLoading;
  if (dataLoading) {
    return <IndeterminateLoading />;
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Accordion disableGutters defaultExpanded square>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="large" />}
          aria-controls="assistants-content"
          id="assistants-header"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: '100%', justifyContent: 'space-between' }}
          >
            <MDTypography variant="h6">
              {msg({ id: 'page.exportConfiguration.tab.assistants.title' })}
            </MDTypography>
            {selectedAssistants.length > 0 ? (
              <MDTypography variant="body2" color="text">
                {msg(
                  { id: 'page.exportConfiguration.selection.count' },
                  { count: selectedAssistants.length }
                )}
              </MDTypography>
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={assistants}
              columns={assistantColumns}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedAssistants}
              onRowSelectionModelChange={setSelectedAssistants}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                density: 'compact',
                sorting: {
                  sortModel: [{ field: 'code', sort: 'asc' }]
                }
              }}
              pageSizeOptions={[5, 20, 50]}
              loading={assistantsLoading}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters square>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="large" />}
          aria-controls="prompts-content"
          id="prompts-header"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: '100%', justifyContent: 'space-between' }}
          >
            <MDTypography variant="h6">
              {msg({ id: 'page.exportConfiguration.tab.prompts.title' })}
            </MDTypography>
            {selectedPrompts.length > 0 ? (
              <MDTypography variant="body2" color="text">
                {msg(
                  { id: 'page.exportConfiguration.selection.count' },
                  { count: selectedPrompts.length }
                )}
              </MDTypography>
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={prompts}
              columns={promptColumns}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedPrompts}
              onRowSelectionModelChange={setSelectedPrompts}
              autosizeOptions={{
                columns: ['id', 'code', 'description'],
                includeHeaders: true,
                includeOutliers: true
              }}
              autosizeOnMount
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                density: 'compact',
                sorting: {
                  sortModel: [{ field: 'code', sort: 'asc' }]
                }
              }}
              pageSizeOptions={[5, 10, 20]}
              loading={promptsLoading}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters square>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="large" />}
          aria-controls="scripts-content"
          id="scripts-header"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: '100%', justifyContent: 'space-between' }}
          >
            <MDTypography variant="h6">
              {msg({ id: 'page.exportConfiguration.tab.scripts.title' })}
            </MDTypography>
            {selectedScripts.length > 0 ? (
              <MDTypography variant="body2" color="text">
                {msg(
                  { id: 'page.exportConfiguration.selection.count' },
                  { count: selectedScripts.length }
                )}
              </MDTypography>
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={scripts}
              columns={scriptColumns}
              getRowId={(row) => row.code}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedScripts}
              onRowSelectionModelChange={setSelectedScripts}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                density: 'compact',
                sorting: {
                  sortModel: [{ field: 'code', sort: 'asc' }]
                }
              }}
              pageSizeOptions={[5, 20, 50]}
              loading={scriptsLoading}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

ExportConfigurationSelection.propTypes = {
  selectedAssistants: PropTypes.array.isRequired,
  setSelectedAssistants: PropTypes.func.isRequired,
  selectedPrompts: PropTypes.array.isRequired,
  setSelectedPrompts: PropTypes.func.isRequired,
  selectedScripts: PropTypes.array.isRequired,
  setSelectedScripts: PropTypes.func.isRequired
};
